/**
 * Pure rules behind the dock remote-debug panel: when DJI's cover_force_close
 * may be offered, and how a failed dock command is described to the operator.
 *
 * Background (prod 2026-09-28): after the aircraft was lost in a crash the
 * regular cover_close always failed, because the dock's documented sequence
 * (get_bid → check_work_mode → check_task_state → check_scram_state →
 * turn_on_drone → drone_paddle_forward → close_cover → …) powers the aircraft
 * on before closing, and an empty pad answers 514109. The panel only flipped
 * the button text, so nobody could see why. DJI provides cover_force_close for
 * exactly this case, restricted to drone_in_dock=0 plus a camera check.
 */

export const COVER_STATE_CLOSED = 0

export interface CoverForceCloseInput {
  /** `basic_osd.drone_in_dock` from the dock OSD; boolean, 0/1 or missing. */
  droneInDock?: boolean | number | string | null
  /** `basic_osd.cover_state` from the dock OSD (0 closed, 1 open, 2 half open, 3 error). */
  coverState?: number | string | null
}

export type CoverForceCloseAvailability = 'ready' | 'pad-not-empty' | 'cover-closed'

export const COVER_FORCE_CLOSE_STATUS_TEXT: Record<CoverForceCloseAvailability, string> = {
  ready: '舱内无飞行器',
  'pad-not-empty': '需舱内无飞行器',
  'cover-closed': '舱盖已关闭',
}

export const COVER_FORCE_CLOSE_CONFIRM_TEXT =
  '强制关舱盖不会给飞行器上电和收桨。请先通过机场摄像头确认停机坪上没有飞行器；' +
  '如果舱内仍有飞行器，关闭舱盖会夹伤桨叶。确认舱内无飞行器后再继续。'

function normalizeFlag (value: boolean | number | string | null | undefined): boolean | null {
  if (value === true || value === false) {
    return value
  }
  if (value === null || value === undefined || value === '') {
    return null
  }
  const number = Number(value)
  return Number.isFinite(number) ? number === 1 : null
}

/**
 * DJI only allows cover_force_close while the dock itself reports the pad as
 * empty; an unknown flag counts as "not empty" so nothing is offered blind.
 */
export function resolveCoverForceCloseAvailability ({ droneInDock, coverState }: CoverForceCloseInput = {}): CoverForceCloseAvailability {
  if (normalizeFlag(droneInDock) !== false) {
    return 'pad-not-empty'
  }
  const cover = coverState === null || coverState === undefined || coverState === '' ? null : Number(coverState)
  if (cover === COVER_STATE_CLOSED) {
    return 'cover-closed'
  }
  return 'ready'
}

/** DJI dock control error texts (Cloud API error-code table, 5141xx). */
export const DOCK_COMMAND_ERROR_TEXT: Record<number, string> = {
  514100: '机场运行异常，请重启机场后重试',
  514101: '推杆闭合失败，请检查停机坪上是否存在异物、飞行器方向是否放反，或重启机场后重试',
  514102: '推杆展开失败，请检查停机坪上是否存在异物，或重启机场后重试',
  514103: '飞行器电量低于 30%，无法执行飞行任务，请充电后重试',
  514104: '飞行器电池开始充电失败，请重启机场后重试',
  514105: '飞行器电池停止充电失败，请重启机场后重试',
  514106: '飞行器电源控制异常，请重启机场后重试',
  514107: '舱盖开启失败，请检查舱盖周围是否存在异物，或重启机场后重试',
  514108: '舱盖关闭失败，请检查舱盖周围是否存在异物，或重启机场后重试',
  514109: '飞行器开机失败，请检查飞行器是否在舱内、电量是否正常，或重启机场后重试；舱内无飞行器时请使用“强制关舱盖”',
  514110: '飞行器关机失败，请重启机场后重试',
  514111: '飞行器慢转收桨控制异常，请重启机场后重试',
  514112: '飞行器慢转收桨控制异常，请重启机场后重试',
  514113: '机场推杆与飞行器无法连接，请检查飞行器是否在舱内、推杆是否卡住、充电连接器是否脏污或损坏',
  514114: '获取飞行器电源状态失败，请重启机场后重试',
  514116: '机场正在执行其他控制指令，请稍后重试',
  514117: '舱盖开启或关闭未到位，请重启机场后重试',
  514118: '推杆展开或闭合未到位，请重启机场后重试',
  514120: '机场与飞行器断连，请重启机场后重试或重新对频',
  514121: '机场急停按钮被按下，请释放急停按钮',
  514122: '获取飞行器充电状态失败，请重启机场后重试',
  514123: '飞行器电池电量过低无法开机',
  514124: '获取飞行器电池信息失败，请重启机场后重试',
  514125: '飞行器电池电量已接近满电，无法开始充电',
  514141: '机场系统运行异常，请重启机场后重试',
  514143: '推杆未闭合或闭合不到位，请稍后重试或重启机场后重试',
  514144: '舱盖未关闭或关闭不到位，请稍后重试或重启机场后重试',
  514145: '机场处于现场调试中，无法执行当前操作，请断开遥控器与机场的数据线后重试',
  514146: '机场处于远程调试中，无法执行飞行任务，请退出远程调试后重试',
  514147: '设备升级中，无法进行远程调试，请等待升级完成后重试',
  514148: '机场已在作业中，无法进行远程调试，请等待当前任务完成后重试',
}

/** One line for a toast: the command label plus the dock's reason, when it gave one. */
export function describeDockCommandFailure (label: string, result: number | null | undefined, status?: string): string {
  const head = `${label}指令${status === 'timeout' ? '超时' : status === 'canceled' ? '已取消' : '失败'}`
  const code = Number(result)
  if (!Number.isFinite(code) || code === 0) {
    return head
  }
  const reason = DOCK_COMMAND_ERROR_TEXT[code]
  return reason ? `${head}：${reason}（${code}）` : `${head}：错误码 ${code}`
}
